import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { ECARD } from "@/constants/testIds";
import { toast } from "sonner";
import { ECARD_LANGUAGES, ecardStrings } from "@/lib/ecardI18n";

const TEMPLATES = [
  { id: "rainbow-bridge", name: "Rainbow Bridge", grad: "linear-gradient(135deg,#FFE7CC,#FFD1DC 30%,#D7E8FF 70%,#E9D7FF)" },
  { id: "golden-clouds", name: "Golden Clouds", grad: "linear-gradient(135deg,#FFF4D6,#F2E3C6,#FDFBF7)" },
  { id: "starlit-meadow", name: "Starlit Meadow", grad: "linear-gradient(135deg,#E6E9F0,#FDFBF7,#F2E3C6)" },
  { id: "celestial-moon", name: "Celestial Moon", grad: "linear-gradient(135deg,#E8ECEF,#F5EAD3,#D4AF37)" },
];

export default function CreateEcard() {
  const nav = useNavigate();
  const [tpl, setTpl] = useState("rainbow-bridge");
  const [language, setLanguage] = useState("English");
  const [f, setF] = useState({
    recipient_name: "",
    recipient_email: "",
    sender_name: "",
    pet_name: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (sendEmail) => {
    if (!f.recipient_name.trim() || !f.sender_name.trim() || !f.message.trim()) {
      toast.error("Recipient, your name, and a message are required.");
      return;
    }
    if (sendEmail && !f.recipient_email.trim()) {
      toast.error("Please enter the recipient's email to send by mail.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/ecards", {
        template: tpl,
        recipient_name: f.recipient_name,
        recipient_email: f.recipient_email || null,
        sender_name: f.sender_name,
        pet_name: f.pet_name || null,
        message: f.message,
        send_email: sendEmail,
        language,
      });
      if (sendEmail) {
        if (data.email_sent) toast.success("eCard delivered ✦");
        else toast.error(data.email_error ? `Email failed: ${data.email_error}` : "Email could not be sent — link is ready instead.");
      } else {
        toast.success("Your card is ready to share ✦");
      }
      nav(`/ecard/${data.share_slug}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not create eCard.");
    } finally {
      setBusy(false);
    }
  };

  const current = TEMPLATES.find((t) => t.id === tpl);

  return (
    <main className="relative max-w-7xl mx-auto px-6 md:px-12 py-16">
      <div className="text-center mb-12">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Sympathy stationery</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--rb-text-heading)]">
          Send a card<br/><span className="italic">worthy of the moment.</span>
        </h1>
        <div className="ornament mt-6"><span>✦</span></div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-5 space-y-6">
          <div className="glass p-8">
            <div className="tracking-celestial text-[var(--rb-text-muted)] mb-5">Choose a stationery</div>
            <div className="grid grid-cols-2 gap-3">
              {TEMPLATES.map((t) => (
                <button key={t.id} onClick={() => setTpl(t.id)} className={`p-4 rounded-sm border transition ${tpl === t.id ? "border-[var(--rb-gold)] shadow-[0_0_24px_rgba(212,175,55,0.25)]" : "border-[var(--rb-gold-soft)]"}`} data-testid={`ecard-template-${t.id}`}>
                  <div className="h-16 rounded-sm mb-2" style={{ background: t.grad }}/>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-text-body)]">{t.name}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="glass p-8 space-y-4">
            <div className="tracking-celestial text-[var(--rb-text-muted)] mb-3">The message</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="rb-label">Recipient *</label>
                <input className="rb-input" value={f.recipient_name} onChange={upd("recipient_name")} placeholder="Sarah" data-testid={ECARD.recipientNameInput}/>
              </div>
              <div>
                <label className="rb-label">Their email</label>
                <input type="email" className="rb-input" value={f.recipient_email} onChange={upd("recipient_email")} placeholder="(optional)" data-testid={ECARD.recipientEmailInput}/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="rb-label">Your name *</label>
                <input className="rb-input" value={f.sender_name} onChange={upd("sender_name")} placeholder="Emma" data-testid={ECARD.senderNameInput}/>
              </div>
              <div>
                <label className="rb-label">Their pet (optional)</label>
                <input className="rb-input" value={f.pet_name} onChange={upd("pet_name")} placeholder="Biscuit" data-testid={ECARD.petNameInput}/>
              </div>
            </div>
            <div>
              <label className="rb-label">Your message *</label>
              <textarea className="rb-textarea" rows={6} value={f.message} onChange={upd("message")} placeholder="Words from your heart..." data-testid={ECARD.messageInput}/>
            </div>
            <div>
              <label className="rb-label">Recipient&apos;s language</label>
              <select
                className="rb-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="ecard-language-select"
              >
                {ECARD_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
              <p className="text-[11px] text-[var(--rb-text-muted)] italic mt-2">
                The card&apos;s greeting, signature, and email subject will appear in this language. Your personal message stays exactly as you wrote it.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => submit(false)} disabled={busy} className="btn-ghost justify-center" data-testid={ECARD.createLinkBtn}>
              {busy ? "Working…" : "Create link only"}
            </button>
            <button onClick={() => submit(true)} disabled={busy} className="btn-gold justify-center" data-testid={ECARD.sendBtn}>
              {busy ? "Sending…" : "Send by email"}
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-7">
          <div className="lg:sticky lg:top-8">
            <EcardPreview data={f} template={current} language={language} />
          </div>
        </div>
      </div>
    </main>
  );
}

export function EcardPreview({ data, template, language }) {
  const s = ecardStrings(language || data?.language);
  return (
    <div className="memorial-card overflow-hidden">
      <div className="h-48 relative" style={{ background: template?.grad }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <svg width="180" height="90" viewBox="0 0 180 90" className="opacity-80">
            <defs>
              <linearGradient id="rb-ec" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#FFB3BA"/>
                <stop offset="25%" stopColor="#FFDFBA"/>
                <stop offset="50%" stopColor="#FFFFBA"/>
                <stop offset="75%" stopColor="#BAFFC9"/>
                <stop offset="100%" stopColor="#BAE1FF"/>
              </linearGradient>
            </defs>
            <path d="M10 80 A 80 80 0 0 1 170 80" fill="none" stroke="url(#rb-ec)" strokeWidth="6" strokeLinecap="round"/>
          </svg>
        </div>
      </div>
      <div className="p-10 md:p-14 text-center">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">
          {s.from} {data.sender_name || "—"}
        </div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--rb-text-heading)]">
          {s.dear} {data.recipient_name || "friend"},
        </h2>
        {data.pet_name && (
          <div className="font-serif-display italic text-[var(--rb-text-muted)] mt-2">
            {s.in_loving_memory_of} {data.pet_name}
          </div>
        )}
        <div className="ornament mt-6 mb-6"><span>✦</span></div>
        <pre className="font-serif-display italic text-[18px] leading-[1.9] text-[var(--rb-text-heading)] whitespace-pre-wrap" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
{data.message || "Your message will appear here, with care."}
        </pre>
        {data.sender_name && (
          <div className="mt-10 text-[12px] tracking-[0.25em] uppercase text-[var(--rb-text-muted)]">
            {s.with_love}, {data.sender_name}
          </div>
        )}
      </div>
    </div>
  );
}
